import { useState } from "react";
import { Hand, CheckCircle2, XCircle, Edit3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "../../lib/utils";

interface InterruptDialogProps {
  nodeId: string;
  nodeName: string;
  reason?: string;
  state: Record<string, unknown>;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onEdit: (editedState: Record<string, unknown>) => void;
  onDismiss: () => void;
}

export function InterruptDialog({
  nodeId,
  nodeName,
  reason,
  state,
  onApprove,
  onReject,
  onEdit,
  onDismiss,
}: InterruptDialogProps) {
  const [mode, setMode] = useState<"review" | "reject" | "edit">("review");
  const [rejectReason, setRejectReason] = useState("");
  const [editJson, setEditJson] = useState(JSON.stringify(state, null, 2));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-[480px] max-h-[80vh] flex flex-col rounded-xl border border-border bg-card shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-orange-500/5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
            <Hand className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">Human Review Required</h3>
            <p className="text-xs text-muted-foreground">
              Agent paused at <span className="font-medium text-foreground">{nodeName}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="ml-auto text-muted-foreground hover:text-foreground text-xs"
          >
            Dismiss
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {reason && (
            <div className="rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/30 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600 dark:text-orange-400 mb-1">Reason</p>
              <p className="text-sm text-foreground">{reason}</p>
            </div>
          )}

          {mode === "review" && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Current State</p>
              <pre className="rounded-lg border border-border bg-muted/30 p-3 text-xs font-mono overflow-x-auto max-h-48 overflow-y-auto">
                {JSON.stringify(state, null, 2)}
              </pre>
            </div>
          )}

          {mode === "reject" && (
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Rejection Reason</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this should be rejected..."
                rows={3}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
            </div>
          )}

          {mode === "edit" && (
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Edit State (JSON)</label>
              <textarea
                value={editJson}
                onChange={(e) => setEditJson(e.target.value)}
                rows={10}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono resize-none focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-border bg-muted/20">
          {mode === "review" && (
            <>
              <Button size="sm" onClick={onApprove} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                Approve
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setMode("reject")}>
                <XCircle className="h-3.5 w-3.5 mr-1.5" />
                Reject
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMode("edit")}>
                <Edit3 className="h-3.5 w-3.5 mr-1.5" />
                Edit &amp; Resume
              </Button>
            </>
          )}
          {mode === "reject" && (
            <>
              <Button size="sm" variant="destructive" onClick={() => onReject(rejectReason)} disabled={!rejectReason.trim()}>
                Confirm Reject
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMode("review")}>
                Back
              </Button>
            </>
          )}
          {mode === "edit" && (
            <>
              <Button
                size="sm"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(editJson);
                    onEdit(parsed);
                  } catch {
                    // invalid JSON — don't submit
                  }
                }}
              >
                Resume with Edits
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMode("review")}>
                Back
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
